/**
 * On-demand loader for the Collect.js (NMI / safewebservices) tokenization
 * script.
 *
 * The script used to be a plain <script> tag in public/index.html, so every
 * page in the app downloaded it and logged the vendor's
 * "Could not create PaymentRequestAbstraction" error — the Collect.js
 * constructor unconditionally builds Apple/Google Pay buttons, which we never
 * configure (MTC-084). It is now injected the first time a payment form mounts.
 *
 * `data-tokenization-key` has to stay on the script tag: the Collect.js
 * constructor reads its own tag's `dataset` and immediately fetches a session
 * token with it, before any `configure()` call can run.
 *
 * The other options that used to be data-* attributes are passed through
 * `configure()` instead (see COLLECT_JS_OPTIONS). That is equivalent — the
 * vendor JSON-parses the string form of `data-placeholder-css` into the same
 * object — and deliberately leaves `data-variant` off the tag: with
 * `variant: 'inline'` set at construction the constructor builds the inline
 * iframes itself, racing the `configure()` call that supplies the real field
 * selectors.
 *
 * Known and accepted: loading on demand means the vendor builds its inline
 * iframes twice on the payment step. Its constructor re-reads `variant` when the
 * session token settles, which is now after our `configure()` has set it, so the
 * constructor builds a set of iframes that `configure()` immediately replaces.
 * The cost is three aborted `inline.php` requests; the resulting DOM and the
 * tokenization it drives are correct.
 */
const COLLECT_JS_SRC = 'https://secure.safewebservices.com/token/Collect.js';
const TOKENIZATION_KEY = 'ksMBQ3-p233H6-3XSA7a-zHz895';

/**
 * How long to wait for the script before giving up. As a tag in index.html it
 * had the whole session to arrive; fetched on demand it is in the submit path,
 * where a stalled request fires neither `onload` nor `onerror` and would leave
 * every awaiting caller pending forever.
 */
const LOAD_TIMEOUT_MS = 15000;

/** Options that were data-* attributes on the public/index.html script tag. */
const COLLECT_JS_OPTIONS = {
	variant: 'inline',
	placeholderCss: { color: '#b3b3b3' },
	googleFont: 'Montserrat:400',
};

let loadPromise = null;
let isConfigured = false;

// Set only inside a successful `onload`, alongside the tag and instance that
// earned it. A timed-out or errored load's `<script>` is removed from the
// DOM, but that does not abort its in-flight fetch or stop it from executing
// later — the vendor still defines `window.CollectJS` off a detached tag.
// Trusting `window.CollectJS` on its own would let that late, untokenizable
// instance poison every subsequent `loadCollectJS()` call via the fast path
// below. Requiring the tag to still be connected, and the global to still be
// the exact instance this load resolved with, rejects both a late-arriving
// failed load and a late-arriving *stale* success (e.g. a first attempt that
// finally lands after a retry already succeeded and got configured).
let readyScript = null;
let readyInstance = null;

/**
 * Injects Collect.js once and resolves with `window.CollectJS`. Concurrent and
 * repeat callers (a remount, a second payment form) share the same promise, so
 * the script tag is only ever added once.
 *
 * @returns {Promise<object>} the global CollectJS instance
 */
export function loadCollectJS() {
	if (
		readyScript?.isConnected &&
		window.CollectJS &&
		window.CollectJS === readyInstance
	) {
		return Promise.resolve(window.CollectJS);
	}

	if (!loadPromise) {
		loadPromise = new Promise((resolve, reject) => {
			const script = document.createElement('script');
			script.src = COLLECT_JS_SRC;
			script.async = true;
			script.dataset.tokenizationKey = TOKENIZATION_KEY;

			let timeoutId;

			// Drop the cached promise and the dead tag so a later mount or submit
			// retries instead of being poisoned by one bad load. Nulling the
			// handlers stops this tag's eventual (unabortable) late execution
			// from resolving/rejecting a promise nobody is awaiting any more;
			// the fast-path check above is what stops it from being trusted if
			// it still goes on to define window.CollectJS.
			const fail = (message) => {
				clearTimeout(timeoutId);
				loadPromise = null;
				script.onload = null;
				script.onerror = null;
				script.remove();
				reject(new Error(message));
			};

			timeoutId = setTimeout(
				() => fail(`Collect.js did not load within ${LOAD_TIMEOUT_MS}ms`),
				LOAD_TIMEOUT_MS,
			);

			script.onload = () => {
				if (!window.CollectJS) {
					fail('Collect.js loaded without defining window.CollectJS');
					return;
				}

				clearTimeout(timeoutId);
				readyScript = script;
				readyInstance = window.CollectJS;
				resolve(window.CollectJS);
			};

			script.onerror = () => fail('Collect.js failed to load');

			document.head.appendChild(script);
		});
	}

	return loadPromise;
}

/**
 * Applies the inline field configuration to a loaded instance. It lives here
 * rather than in the hook so that the "has been configured" flag cannot drift
 * from the `configure()` call it describes — `startPaymentRequest()` relies on
 * it to tell a usable instance from a freshly loaded one.
 *
 * @param {object} collectJS the instance from `loadCollectJS()`
 * @param {object} options callback/fields options from `useCollectJS`
 */
export function configureFields(collectJS, options) {
	collectJS.configure({ ...COLLECT_JS_OPTIONS, ...options });
	isConfigured = true;
}

/**
 * Hands the collected card/bank fields to Collect.js for tokenization, waiting
 * for the script to finish loading first. The resulting token arrives through
 * the `callback` wired up by `useCollectJS`.
 *
 * @returns {Promise<void>} rejects if Collect.js could not be loaded, or loaded
 *   but was never configured. The unconfigured case is reachable: a load that
 *   fails while the form is mounted clears the cached promise, so this call
 *   injects a second tag, and if that one succeeds nothing is left to configure
 *   it. The vendor returns `undefined` from `startPaymentRequest()` on such an
 *   instance and never fires a callback, which would resolve here and leave the
 *   caller's spinner up forever.
 */
export function startPaymentRequest() {
	return loadCollectJS().then((collectJS) => {
		if (!isConfigured) {
			throw new Error('Collect.js was loaded but never configured');
		}

		return collectJS.startPaymentRequest();
	});
}

export const TEST_ONLY = {
	COLLECT_JS_SRC,
	TOKENIZATION_KEY,
	LOAD_TIMEOUT_MS,
	/** Returns the module and the document to their pre-load state. */
	reset: () => {
		loadPromise = null;
		isConfigured = false;
		readyScript = null;
		readyInstance = null;
		delete window.CollectJS;
		document
			.querySelectorAll(`script[src="${COLLECT_JS_SRC}"]`)
			.forEach((script) => script.remove());
	},
};
